import React, { useState, useMemo } from 'react';
import { Radio, Plus, AlertTriangle, CheckCircle2, Check, X, Lock, Upload } from 'lucide-react';
import { useSecurityStore } from '../../../store/securityStore';
import { useAuthStore } from '../../../store/authStore';
import { RowActions } from '../../../components/RowActions';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

const COUNTRY_OPTIONS = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IN', name: 'India' },
  { code: 'DE', name: 'Germany' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'JP', name: 'Japan' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AE', name: 'United Arab Emirates' },
];

export function ThreatProtectionSubTab() {
  const { 
    ipRules, geoFenceRules, anomalySettings, anomalyAlerts, securityAuditLogs,
    createIPRule, deleteIPRule, toggleIPRule, bulkImportIPs,
    updateGeoFenceRules, updateAnomalySettings, resolveAnomalyAlert, lockUserFromAlert 
  } = useSecurityStore();
  const { user } = useAuthStore();

  const isSuperAdmin = (user?.roles || []).some((r: any) => 
    typeof r === 'string' 
      ? r.toLowerCase().includes('admin') || r.toLowerCase().includes('super')
      : r.code === 'super_admin' || r.name === 'Super Admin'
  );

  const [activeSubTab, setActiveSubTab] = useState<'ip' | 'geo' | 'anomaly' | 'audit'>('ip');
  const [ipFilterType, setIpFilterType] = useState<'all' | 'allow' | 'block'>('all');

  // IP Modals
  const [isIpModalOpen, setIsIpModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [ipForm, setIpForm] = useState({
    type: 'block' as 'allow' | 'block',
    ipOrCidr: '',
    note: '',
    expiresAt: 'Never',
  });
  const [bulkText, setBulkText] = useState('');
  const [bulkType, setBulkType] = useState<'allow' | 'block'>('block');

  // Simulated Admin IP (to check if admin would block themselves)
  const currentAdminIp = '198.51.100.22';

  const isSelfBlockWarning = useMemo(() => {
    if (ipForm.type !== 'block') return false;
    const target = ipForm.ipOrCidr.trim();
    if (!target) return false;
    if (target === currentAdminIp) return true;
    if (target.includes('/') && currentAdminIp.startsWith(target.split('.')[0])) return true;
    return false;
  }, [ipForm, currentAdminIp]);

  const handleSaveIp = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanIp = ipForm.ipOrCidr.trim();
    if (!cleanIp) {
      toast.error('IP address or CIDR range is required');
      return;
    }

    // IP/CIDR format validation
    const ipRegex = /^(([0-9]{1,3}\.){3}[0-9]{1,3}(\/([0-9]|[1-2][0-9]|3[0-2]))?)$|^([0-9a-fA-F:]+(\/[0-9]{1,3})?)$/;
    if (!ipRegex.test(cleanIp)) {
      toast.error('Invalid IP address or CIDR format. (e.g. 192.168.1.1 or 10.0.0.0/16)');
      return;
    }

    if (isSelfBlockWarning) {
      const proceed = window.confirm(`WARNING: The IP you are about to block (${cleanIp}) matches or includes your current administrative connection (${currentAdminIp}). Doing so could lock you out. Proceed anyway?`);
      if (!proceed) return;
    }

    await createIPRule({
      type: ipForm.type,
      ipOrCidr: cleanIp,
      note: ipForm.note.trim() || 'Manual restriction',
      expiresAt: ipForm.expiresAt,
      addedBy: user?.email || 'Administrator',
      status: 'active',
    });

    setIsIpModalOpen(false);
    setIpForm({ type: 'block', ipOrCidr: '', note: '', expiresAt: 'Never' });
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) {
      toast.error('Please enter at least one IP or CIDR');
      return;
    }
    await bulkImportIPs(bulkText, bulkType);
    setBulkText('');
    setIsBulkModalOpen(false);
  };

  const toggleGeoCountry = (code: string) => {
    if (!isSuperAdmin) return;
    const exists = geoFenceRules.countries.includes(code);
    const updated = exists
      ? geoFenceRules.countries.filter((c) => c !== code)
      : [...geoFenceRules.countries, code];
    updateGeoFenceRules({ countries: updated });
  };

  const filteredIpRules = useMemo(() => {
    if (ipFilterType === 'all') return ipRules;
    return ipRules.filter((r) => r.type === ipFilterType);
  }, [ipRules, ipFilterType]);

  const threatAuditLogs = useMemo(() => {
    return securityAuditLogs.filter((l) => l.section === 'threat_protection');
  }, [securityAuditLogs]);

  return (
    <div className="space-y-6">
      {/* Sub-tab Navigation */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {[
          { id: 'ip', label: `IP Controls (${ipRules.length} Entries)` },
          { id: 'geo', label: `Geo-Fencing (${geoFenceRules.countries.length} Regions)` },
          { id: 'anomaly', label: `Threat & Anomaly Detection (${anomalyAlerts.length} Alerts)` },
          { id: 'audit', label: `Threat Audit Trail (${threatAuditLogs.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveSubTab(tab.id as any)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
              activeSubTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 1. IP CONTROLS */}
      {activeSubTab === 'ip' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Network Layer IP Allowlists &amp; Threat Blocklists
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Restrict origin IP addresses and CIDR subnets at API gateway edge before reaching application clusters.
              </p>
            </div>
            {isSuperAdmin && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsBulkModalOpen(true)}
                  className="gap-1.5 h-8.5 shrink-0"
                >
                  <Upload className="size-3.5" />
                  <span>Bulk Import</span>
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setIsIpModalOpen(true)}
                  className="gap-1.5 h-8.5 shrink-0"
                >
                  <Plus className="size-3.5" />
                  <span>Add IP / CIDR</span>
                </Button>
              </div>
            )}
          </div>

          {/* Current IP Safety Indicator */}
          <div className="p-3.5 rounded-xl border border-border bg-muted/20 flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Radio className="size-4 text-emerald-500 animate-pulse" />
              <span>
                Your Current Admin Connection IP: <strong className="text-foreground font-mono">{currentAdminIp}</strong>
              </span>
            </div>
            <span className="text-3xs text-muted-foreground">
              Self-block protection active · Subnet checks enabled
            </span>
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-2">
            {(['all', 'allow', 'block'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setIpFilterType(t)}
                className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer border transition-colors ${
                  ipFilterType === t
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:text-foreground'
                }`}
              >
                {t === 'all' ? 'All Rules' : t === 'allow' ? 'Allowlist (Trusted)' : 'Blocklist (Blocked)'}
              </button>
            ))}
          </div>

          {/* IP Rules Table */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
            <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
              <h3 className="font-semibold text-xs text-foreground">Configured Network Ranges</h3>
              <span className="text-xs font-semibold text-muted-foreground">{filteredIpRules.length} rules</span>
            </div>

            <div className="divide-y divide-border">
              {filteredIpRules.map((rule) => (
                <div key={rule.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-foreground">{rule.ipOrCidr}</span>
                      <span
                        className={`px-2 py-0.2 rounded-full font-bold text-3xs uppercase tracking-wider ${
                          rule.type === 'allow'
                            ? 'bg-success/10 text-success-strong border border-success/30'
                            : 'bg-destructive/10 text-destructive border border-destructive/20'
                        }`}
                      >
                        {rule.type}
                      </span>
                      <span
                        className={`px-2 py-0.2 rounded-full font-semibold text-3xs uppercase tracking-wider ${
                          rule.status === 'active'
                            ? 'bg-muted text-foreground border border-border'
                            : 'bg-muted/50 text-muted-foreground'
                        }`}
                      >
                        {rule.status}
                      </span>
                    </div>
                    <p className="text-3xs text-muted-foreground">
                      Note: {rule.note} · Added by: {rule.addedBy} · Expiration: {rule.expiresAt}
                    </p>
                  </div>

                  <RowActions
                    entityName={rule.ipOrCidr}
                    onToggleStatus={isSuperAdmin ? () => toggleIPRule(rule.id) : undefined}
                    isStatusActive={rule.status === 'active'}
                    onDelete={isSuperAdmin ? () => deleteIPRule(rule.id) : undefined}
                    readOnly={!isSuperAdmin}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 2. GEO-FENCING */}
      {activeSubTab === 'geo' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
            <div>
              <h2 className="font-display text-sm font-semibold text-foreground">
                Geographic Access Boundaries &amp; Country Fencing
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Block or require step-up authentication for incoming sessions originating outside permitted operating jurisdictions.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Allowed Countries List */}
            <div className="lg:col-span-2 p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-border">
                <h3 className="font-semibold text-xs text-foreground">
                  Permitted Operating Countries ({geoFenceRules.countries.length} Allowed)
                </h3>
                <span className="text-3xs text-muted-foreground">Click country chip to toggle access</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {COUNTRY_OPTIONS.map((c) => {
                  const isAllowed = geoFenceRules.countries.includes(c.code);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      disabled={!isSuperAdmin}
                      onClick={() => toggleGeoCountry(c.code)}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                        isAllowed
                          ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                          : 'bg-muted/40 text-muted-foreground border-border hover:text-foreground'
                      }`}
                    >
                      <span>{c.name}</span>
                      <span className="font-mono text-3xs opacity-80">({c.code})</span>
                      {isAllowed ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Violation Policy & Exceptions */}
            <div className="p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
              <h3 className="font-semibold text-xs text-foreground pb-2 border-b border-border">
                Violation Action &amp; Exceptions
              </h3>

              <div>
                <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                  Action on Geolocation Breach
                </label>
                <select
                  disabled={!isSuperAdmin}
                  value={geoFenceRules.action}
                  onChange={(e) => updateGeoFenceRules({ action: e.target.value as any })}
                  className="w-full h-8.5 rounded-lg border border-border bg-background px-2.5 text-xs text-foreground outline-none cursor-pointer"
                >
                  <option value="block">Block Access Immediately</option>
                  <option value="require_mfa">Require Step-Up MFA Challenge</option>
                  <option value="alert">Allow But Alert Security Team</option>
                </select>
              </div>

              <div>
                <label className="text-3xs font-semibold uppercase text-muted-foreground block mb-1">
                  Whitelisted Travelling Executives
                </label>
                <div className="space-y-1 text-xs">
                  {geoFenceRules.exceptions.users.map((email) => (
                    <span key={email} className="inline-block bg-muted px-2 py-0.5 rounded text-3xs font-mono mr-1 mb-1">
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. ANOMALY DETECTION & RECENT ALERTS */}
      {activeSubTab === 'anomaly' && (
        <div className="space-y-6">
          <div>
            <h2 className="font-display text-sm font-semibold text-foreground">
              Machine Learning Anomaly Detection &amp; Threat Alerts
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Automated behavioral heuristic analysis to detect impossible travel, credential stuffing, and unusual activity spikes.
            </p>
          </div>

          {/* Anomaly Controls Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              {
                id: 'impossibleTravel',
                title: 'Impossible Travel Velocity',
                desc: 'Two logins from geographically distant IPs in impossible duration',
                data: anomalySettings.impossibleTravel,
              },
              {
                id: 'bruteForce',
                title: 'Brute Force & Credential Spray',
                desc: 'Multiple consecutive failed attempts from single origin',
                data: anomalySettings.bruteForce,
              },
              {
                id: 'massDataExport',
                title: 'Mass Data Extraction',
                desc: 'Export volume exceeding baseline percentile threshold',
                data: anomalySettings.massDataExport,
              },
              {
                id: 'unusualHours',
                title: 'Off-Hours Login Activity',
                desc: 'Logins initiated outside normal user working patterns',
                data: anomalySettings.unusualHours,
              },
              {
                id: 'newDevice',
                title: 'Unrecognized Device Fingerprint',
                desc: 'New browser header, canvas hash, or OS signature',
                data: anomalySettings.newDevice,
              },
            ].map((setting) => (
              <div key={setting.id} className="p-4 rounded-xl border border-border bg-card shadow-xs space-y-2.5">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-xs text-foreground">{setting.title}</h4>
                  <input
                    type="checkbox"
                    disabled={!isSuperAdmin}
                    checked={setting.data.enabled}
                    onChange={(e) => {
                      updateAnomalySettings({
                        [setting.id]: { ...setting.data, enabled: e.target.checked },
                      });
                    }}
                    className="size-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                  />
                </div>
                <p className="text-3xs text-muted-foreground">{setting.desc}</p>
                <div className="pt-2 border-t border-border flex items-center justify-between text-3xs">
                  <span className="text-muted-foreground">Sensitivity:</span>
                  <select
                    disabled={!isSuperAdmin}
                    value={setting.data.sensitivity}
                    onChange={(e) => {
                      updateAnomalySettings({
                        [setting.id]: { ...setting.data, sensitivity: e.target.value as any },
                      });
                    }}
                    className="h-6 px-1 rounded border border-border bg-background text-3xs font-semibold text-foreground outline-none cursor-pointer"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Alerts Table */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
            <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
              <h3 className="font-semibold text-xs text-foreground">Recent Security Anomaly Alerts</h3>
              <span className="text-xs font-semibold text-muted-foreground">{anomalyAlerts.length} alerts</span>
            </div>

            <div className="divide-y divide-border">
              {anomalyAlerts.map((alert) => (
                <div key={alert.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{alert.type}</span>
                      <span className={`px-2 py-0.2 rounded-full font-bold text-3xs ${
                        alert.riskScore >= 80 ? 'bg-destructive/10 text-destructive' : 'bg-amber-500/10 text-amber-600'
                      }`}>
                        Risk Score: {alert.riskScore}/100
                      </span>
                      <span
                        className={`px-2 py-0.2 rounded-full font-bold text-3xs uppercase tracking-wider ${
                          alert.status === 'open'
                            ? 'bg-destructive/10 text-destructive border border-destructive/20'
                            : 'bg-success/10 text-success-strong border border-success/30'
                        }`}
                      >
                        {alert.status}
                      </span>
                    </div>
                    <p className="text-3xs text-muted-foreground">
                      Target User: <strong className="text-foreground">{alert.userEmail}</strong> · Origin: {alert.location} ({alert.ipAddress})
                    </p>
                    <p className="text-3xs text-muted-foreground font-mono mt-0.5 bg-muted/40 p-1 rounded inline-block">
                      Action taken: {alert.actionTaken}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                    <span className="text-3xs text-muted-foreground mr-2">
                      {new Date(alert.detectedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>

                    {isSuperAdmin && alert.status !== 'resolved' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => lockUserFromAlert(alert.id, alert.userEmail)}
                          className="h-7 text-3xs text-destructive hover:bg-destructive/10"
                        >
                          Lock Account
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resolveAnomalyAlert(alert.id)}
                          className="h-7 text-3xs"
                        >
                          Resolve
                        </Button>
                      </>
                    )}

                    <RowActions
                      entityName={`Alert: ${alert.type} (${alert.userEmail})`}
                      onViewDetails={() => toast.success(`Alert: ${alert.type} from ${alert.location}`)}
                      customActions={
                        isSuperAdmin && alert.status !== 'resolved'
                          ? [
                              {
                                label: 'Lock Target Account',
                                icon: <Lock className="size-3.5 text-destructive" />,
                                destructive: true,
                                onClick: () => lockUserFromAlert(alert.id, alert.userEmail),
                              },
                              {
                                label: 'Resolve Alert',
                                icon: <CheckCircle2 className="size-3.5 text-success" />,
                                onClick: () => resolveAnomalyAlert(alert.id),
                              },
                            ]
                          : []
                      }
                      readOnly={!isSuperAdmin}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 4. AUDIT TRAIL TAB */}
      {activeSubTab === 'audit' && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
          <div className="p-4 border-b border-border bg-muted/10 flex items-center justify-between">
            <h3 className="font-semibold text-xs text-foreground">Threat Defense Audit Trail</h3>
            <span className="text-xs font-semibold text-muted-foreground">{threatAuditLogs.length} events</span>
          </div>

          <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
            {threatAuditLogs.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-xs">No threat defense audit logs</div>
            ) : (
              threatAuditLogs.map((log) => (
                <div key={log.id} className="p-3.5 hover:bg-muted/10 text-xs">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-foreground">{log.action}</p>
                      <p className="text-3xs text-muted-foreground mt-0.5">
                        Target: <span className="text-primary font-semibold">{log.target}</span> · Performed by {log.performedBy}
                      </p>
                    </div>
                    <span className="text-3xs text-muted-foreground shrink-0">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Add Single IP Drawer (Opens on Right) */}
      {isIpModalOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setIsIpModalOpen(false)}
        >
          <div
            className="w-full max-w-md bg-card border-l border-border h-full flex flex-col justify-between p-6 shadow-2xl animate-in slide-in-from-right duration-200 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="font-display text-base font-semibold text-foreground">Configure Network Range</h2>
              <button
                type="button"
                onClick={() => setIsIpModalOpen(false)}
                className="size-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveIp} className="space-y-3.5">
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">List Type</label>
                <select
                  value={ipForm.type}
                  onChange={(e) => setIpForm({ ...ipForm, type: e.target.value as any })}
                  className="w-full h-8.5 px-2.5 rounded-lg border border-border bg-background text-xs font-semibold text-foreground outline-none cursor-pointer"
                >
                  <option value="block">Blocklist (Deny Edge Ingress)</option>
                  <option value="allow">Allowlist (Trusted IP Bypass)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">IP Address or CIDR Range</label>
                <input
                  type="text"
                  value={ipForm.ipOrCidr}
                  onChange={(e) => setIpForm({ ...ipForm, ipOrCidr: e.target.value })}
                  placeholder="e.g. 192.168.1.100 or 10.0.0.0/16"
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs font-mono text-foreground outline-none focus:border-primary"
                  required
                />
              </div>

              {isSelfBlockWarning && (
                <div className="p-3 rounded-xl border border-destructive/30 bg-destructive/10 text-xs text-destructive space-y-1">
                  <div className="flex items-center gap-1.5 font-bold">
                    <AlertTriangle className="size-4 shrink-0" />
                    <span>Warning: Self-Lockout Risk</span>
                  </div>
                  <p className="text-3xs leading-relaxed">
                    The IP you typed matches your current IP ({currentAdminIp}). Blocking this IP will terminate your administrative session!
                  </p>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Description / Security Note</label>
                <input
                  type="text"
                  value={ipForm.note}
                  onChange={(e) => setIpForm({ ...ipForm, note: e.target.value })}
                  placeholder="e.g. Tor exit node subnet or London HQ router"
                  className="w-full h-8.5 px-3 rounded-lg border border-border bg-background text-xs text-foreground outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Rule Expiry</label>
                <select
                  value={ipForm.expiresAt}
                  onChange={(e) => setIpForm({ ...ipForm, expiresAt: e.target.value })}
                  className="w-full h-8.5 px-2.5 rounded-lg border border-border bg-background text-xs text-foreground outline-none cursor-pointer"
                >
                  <option value="Never">Permanent (Never Expire)</option>
                  <option value="24 Hours">24 Hours (Temporary Quarantine)</option>
                  <option value="7 Days">7 Days</option>
                  <option value="30 Days">30 Days</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-border">
                <Button variant="outline" size="sm" type="button" onClick={() => setIsIpModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Save Rule
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Drawer (Opens on Right) */}
      {isBulkModalOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setIsBulkModalOpen(false)}
        >
          <div
            className="w-full max-w-md bg-card border-l border-border h-full flex flex-col justify-between p-6 shadow-2xl animate-in slide-in-from-right duration-200 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-border">
              <h2 className="font-display text-base font-semibold text-foreground">Bulk Import IP Ranges</h2>
              <button
                type="button"
                onClick={() => setIsBulkModalOpen(false)}
                className="size-8 rounded-lg hover:bg-muted text-muted-foreground flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="space-y-3.5">
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">Target List</label>
                <select
                  value={bulkType}
                  onChange={(e) => setBulkType(e.target.value as any)}
                  className="w-full h-8.5 px-2.5 rounded-lg border border-border bg-background text-xs font-semibold text-foreground outline-none cursor-pointer"
                >
                  <option value="block">Blocklist</option>
                  <option value="allow">Allowlist</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">
                  Enter IPs or CIDRs (newline or comma separated)
                </label>
                <textarea
                  rows={6}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder="198.51.100.1&#10;198.51.100.2&#10;203.0.113.0/24"
                  className="w-full p-3 rounded-lg border border-border bg-background text-xs font-mono text-foreground outline-none focus:border-primary resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border">
                <Button variant="outline" size="sm" onClick={() => setIsBulkModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={handleBulkImport}>
                  Import Entries
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
